/**
 * UpdatePrompt — banner that surfaces a waiting service worker.
 *
 * Mirrors `InstallPrompt`'s shape: subscribes to a side-effecty
 * module (`lib/sw.ts`), renders nothing when there's nothing to
 * say, and only mounts the actual banner once `lib/sw` reports a
 * waiting SW.
 *
 * Why a user-driven prompt rather than auto-reload (the previous
 * behaviour, where the SW called `skipWaiting()` unconditionally
 * inside `install`): web.dev's PWA update guidance is explicit that
 * forcing a reload can drop unsaved input — half-typed replies in
 * the composer, scroll position in long inboxes. The "Reload"
 * button gives the user a 1-click opt-in instead.
 *
 * Visual: matches `InstallPrompt`. No dismiss affordance — the user
 * is GOING to reload eventually (closing the tab counts; the new
 * SW is already waiting and takes over on next visit). A persistent
 * banner is a stronger nudge than the install prompt's "you can
 * decline forever" affordance.
 */

import { Box, Button, Text } from "@reflecto/client-ui";
import { RefreshCw01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { messages } from "../i18n";
import { applyUpdate, isUpdateAvailable, subscribeUpdateAvailability } from "../lib/sw";

export function UpdatePrompt() {
  const [available, setAvailable] = useState(isUpdateAvailable);

  useEffect(() => {
    // Re-read on subscribe to cover the case where the waiting
    // worker was detected between the initial render and effect
    // commit (the SW registers after the React tree mounts).
    setAvailable(isUpdateAvailable());
    return subscribeUpdateAvailability(setAvailable);
  }, []);

  if (!available) return null;

  // `applyUpdate` is a stable module-level export — no `useCallback`
  // needed. It posts SKIP_WAITING to the waiting SW; the page does
  // NOT reload here. `lib/sw.ts` listens for `controllerchange` and
  // reloads only after the new SW takes over, so we don't race the
  // activation and serve stale assets.
  return (
    <Box direction="row" align="center" justify="between" gap="md" paddingY="sm" paddingX="md">
      <Box direction="column" gap="xs">
        <Text size="m" weight="medium">
          {messages.update.title}
        </Text>
        <Text size="s" tone="secondary">
          {messages.update.description}
        </Text>
      </Box>
      <Button variant="primary" size="s" leadingIcon={RefreshCw01} onClick={applyUpdate}>
        {messages.update.reload}
      </Button>
    </Box>
  );
}
